import { Loader } from "pixel";
import { createContext, useEffect, useMemo, useState } from "react";
import FullPageError from "../../components/display/FullPageError";
import { reportErrorToSentry } from "../../global/sentry/sentry";
import { activeTenantLocalStorage, getErrorDescription, parseJWT } from "../../utils/utils";
import { useAuth } from "../auth/useAuth";
import appConfig from "../../config/appConfig";
import { UserInfoResponse } from "./types";
import { ADMIN_ROLES } from "../../utils/constants";

export type UserInfoContextType = {
  data: {
    userInfo: UserInfoResponse | null;
    activeTenant: string | null;
    tenantOptions?: { label: string; value: string; id: string }[];
    isAdminUserWithoutTenants: boolean;
  };
  isLoading: boolean;
  error: string | null;
  updateActiveTenant: (tenantName: string) => void;
  updateActiveTenantWithoutReload: (tenantName: string) => void;
};
export const UserInfoContext = createContext<UserInfoContextType | null>(null);

type UserInfoProviderProps = {
  children: React.ReactNode;
};
function UserInfoProvider({ children }: Readonly<UserInfoProviderProps>) {
  const auth = useAuth();
  const apiConfig = appConfig?.api;

  const [userInfo, setUserInfo] = useState<UserInfoResponse | null>(null);
  const [activeTenant, setActiveTenant] = useState(() =>
    activeTenantLocalStorage.getActiveTenant()
  );
  const [isAdminUserWithoutTenants, setIsAdminUserWithoutTenants] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenantOptions = useMemo(() => {
    return userInfo?.tenants?.map((tenant) => {
      return { label: tenant, value: tenant, id: tenant };
    });
  }, [userInfo?.tenants]);

  const fetchUserInfo = async (accessToken: string): Promise<UserInfoResponse> => {
    const baseUrl = apiConfig?.api_base_url;
    const tokenIssuerSplit = parseJWT(auth.accessToken as string)?.iss.split("/");
    const authRealm = tokenIssuerSplit[tokenIssuerSplit.length - 1];
    const url = `${baseUrl}/auth/realms/${authRealm}/protocol/openid-connect/userinfo`;

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    // fetch does NOT throw on 404/500
    if (!res.ok) {
      let errorMessage = `HTTP ${res.status}`;

      // Try reading server error response
      try {
        const errorData = await res.json();
        errorMessage = getErrorDescription(errorData);
      } catch {
        // response wasn't JSON
        const text = await res.text().catch(() => "");
        if (text) errorMessage = text;
      }

      throw new Error(errorMessage);
    }

    return res.json();
  };

  const updateActiveTenant = (tenantName: string) => {
    setActiveTenant(tenantName);
    activeTenantLocalStorage.setActiveTenant(tenantName);
    window.location.reload(); // reinitialise fpapi with new tenant and fetch tenant specific data
  };

  const updateActiveTenantWithoutReload = (tenantName: string) => {
    setActiveTenant(tenantName);
    activeTenantLocalStorage.setActiveTenant(tenantName);
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        if (auth.accessToken) {
          const data = await fetchUserInfo(auth.accessToken);

          const defaultUserInfo: UserInfoResponse = {
            sub: "",
            tenants: [],
            email_verified: false,
            roles: [],
            given_name: "",
            family_name: "",
            email: "",
            type: "" as any,
          };

          const tenants = data?.tenants;
          const roles = data?.roles;

          const isAdminUser = ADMIN_ROLES.some((role) => roles?.includes(role));
          const isAdminUserWithoutTenants = isAdminUser && !tenants?.length;
          setIsAdminUserWithoutTenants(isAdminUserWithoutTenants);

          if (
            (!activeTenant && tenants?.length > 0) ||
            (activeTenant && tenants?.length > 0 && !tenants?.includes(activeTenant)) ||
            (activeTenant !== "undefined" && tenants?.length === 0)
          ) {
            // activeTenant present in localStorage but not in user info. This means user has switched to a different tenant
            updateActiveTenant(tenants?.[0]);
            return;
          }

          setUserInfo({ ...defaultUserInfo, ...data });
        }
      } catch (e) {
        window.debug.error(e);
        reportErrorToSentry(e);
        setError("Error fetching user info");
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.accessToken]);

  const value = useMemo(
    () => ({
      data: { userInfo, activeTenant, tenantOptions, isAdminUserWithoutTenants },
      isLoading,
      error,
      updateActiveTenant,
      updateActiveTenantWithoutReload,
    }),
    [activeTenant, error, userInfo, isLoading, tenantOptions, isAdminUserWithoutTenants]
  );

  if (error) {
    return (
      <FullPageError
        errorTitle={error}
        errorDescription="Retry or check back later"
        actionText="Retry"
        action={() => window.location.reload()}
        type="ERROR"
      />
    );
  }

  return (
    <UserInfoContext.Provider value={value}>
      {isLoading && <Loader variant="fullpage" />}
      {children}
    </UserInfoContext.Provider>
  );
}

export default UserInfoProvider;
